import { useMemo } from 'react';

import { aiAgentsApi, AiAgentListItem } from 'apis/aiAgents';
import { useServerStorage } from 'utils/useServerStorage';

interface UseDraftAiAgentsOptions {
  /** Whether to skip loading the AI agents. */
  skip?: boolean;
}

export interface UseDraftAiAgentsResult {
  /** The AI agents that can draft replies. */
  agents: AiAgentListItem[];
  /** The AI agent id set in the agent copilot preferences. */
  defaultAgentId: string | undefined;
  /** The AI agent the preferences point at, if it still exists. */
  defaultAgent: AiAgentListItem | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** A hook for the AI agents that can draft replies and the default one. */
export function useDraftAiAgents({
  skip,
}: UseDraftAiAgentsOptions = {}): UseDraftAiAgentsResult {
  const { data, isLoading, isError } = aiAgentsApi.useReadAiAgentsListQuery(
    {},
    { skip },
  );
  const { data: userStorageData } = useServerStorage('user');
  const defaultAgentId = userStorageData.agent_copilot?.defaultDraftAgentId;

  const agents = useMemo(
    () =>
      (data?.data?.agents ?? [])
        .filter((agent) => agent.variant !== 'automationbot')
        .reverse(),
    [data],
  );

  return {
    agents,
    defaultAgentId,
    defaultAgent: agents.find((agent) => agent.id === defaultAgentId),
    isLoading,
    isError,
  };
}
